import { useMutation, useQuery } from '@apollo/client/react';
import { Button, ButtonProps } from '@chakra-ui/react';
import { GroupEvent, UpsertGroupEventAttendanceInput } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import { omit } from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import { LuCalendarPlus, LuCheck, LuCalendarMinus } from 'react-icons/lu';
type AttendEventButtonProps = {
  event: GroupEvent;
  buttonProps?: ButtonProps;
  ctaLabel?: string;
};
const upsertGroupEventAttendanceMutation = gql`
  mutation ($input: UpsertGroupEventAttendanceInput!) {
    upsertGroupEventAttendance(input: $input) {
      groupEvent {
        id
        groupEventId
        isCurrentUserAttending
        attendanceCount
        maximumAttendeeLimit
        maximumAttendeesRemaining
        attendingUsers {
          id
          items {
            id
            firstName
            lastName
            pictureUrl
            shortBio
            slug
          }
        }
      }
      groupEventAttendance {
        id
      }
    }
  }
`;
export const AttendEventButton = (props: AttendEventButtonProps) => {
  const [toggling, setToggling] = useState(false);
  const query = useQuery(AttendEventButton.queries.groupEvent(), {
    variables: {
      groupEventId: props.event.groupEventId
    }
  });
  const event: GroupEvent = query.data?.groupEvent;
  const [upsertGroupEventAttendance] = useMutation(upsertGroupEventAttendanceMutation);
  const onToggle = async (attending: boolean) => {
    const input: UpsertGroupEventAttendanceInput = {
      groupEventId: event.groupEventId,
      attending
    };
    try {
      setToggling(true);
      await upsertGroupEventAttendance({
        variables: {
          input
        }
      });
      await query.refetch();
    } catch (err) {
      alert(`Your attendance failed to save: ${err}`);
      console.log(err);
    } finally {
      setToggling(false);
    }
  };
  if (query.loading || !event) {
    return <Button loading={true} width={'full'} {...props.buttonProps ? props.buttonProps : {}}>
        {props.ctaLabel || 'Attend'}
      </Button>;
  }
  if (moment(event.endsAt).isBefore(moment())) {
    return <Button disabled={true} width={'full'} {...props.buttonProps ? omit(props.buttonProps, ['bgColor', 'color']) : {}} cursor={'not-allowed'} variant={'outline'}>
        Event Ended
      </Button>;
  }
  const isAttending = !!event.isCurrentUserAttending;
  const overCapacity = event.maximumAttendeeLimit != null && event.maximumAttendeesRemaining != null && event.maximumAttendeesRemaining <= 0 && !isAttending;
  return <Button bgColor={isAttending ? 'green.600' : ''} flexGrow={1} loading={toggling} onClick={async () => {
    await onToggle(!isAttending);
  }} {...isAttending ? {
    _hover: {
      bgColor: 'red.500'
    }
  } : {}} {...props.buttonProps ? props.buttonProps : {}} title={overCapacity ? 'This event is at or over soft capacity — you can still RSVP' : undefined}>
      {isAttending ? <LuCheck /> : overCapacity ? <LuCalendarPlus /> : <LuCalendarPlus />}
      {isAttending ? 'Attending' : props.ctaLabel || (event.isFree ? 'Attend for Free' : overCapacity ? 'Attend (over capacity)' : 'Attend')}
      {isAttending ? null : null}
    </Button>;
};

// Keep cancel icon available for future styling variants
void LuCalendarMinus;
AttendEventButton.queries = {
  groupEvent: () => gql`
    query ($groupEventId: String) {
      groupEvent(groupEventId: $groupEventId) {
        id
        groupEventId
        endsAt
        isCurrentUserAttending
        isFree
        maximumAttendeeLimit
        maximumAttendeesRemaining
        attendanceCount
      }
    }
  `
};